import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type CombatRange, type KillGrowthClass } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatGrowthPct, KILL_GROWTH_CLASS_META } from '@/lib/killGrowth';
import { COMBAT_PROFILE_META, formatEfficiency } from '@/lib/combatEfficiency';
import { Loading, Panel, Segmented, Th, fmtDate, fmtM, playerLabel } from './shared';

// Plan 053 — kills gained per M of strength, compared within power classes.

const CLASS_ORDER: KillGrowthClass[] = ['rising', 'mid', 'top'];
type SortKey = 'index' | 'kills' | 'growth';

export default function CombatTab() {
  const [range, setRange] = useState<CombatRange>('90d');
  const [filter, setFilter] = useState<KillGrowthClass | 'all'>('all');
  const [sort, setSort] = useState<SortKey>('index');
  const q = useQuery({
    queryKey: ['insights', 'combat', range],
    queryFn: () => api.getAdminCombatEfficiency(range),
  });
  const rows = useMemo(() => {
    const list = (q.data?.players ?? []).filter((r) => filter === 'all' || r.class === filter);
    const key = (r: (typeof list)[number]) =>
      sort === 'index' ? (r.index ?? -1) : sort === 'kills' ? r.deltaKillsM : (r.growthPct ?? -Infinity);
    return [...list].sort((a, b) => key(b) - key(a));
  }, [q.data, filter, sort]);

  if (q.isLoading || !q.data) return <Loading />;
  const data = q.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-400">
          Kills gained {fmtDate(data.startIso)} – {fmtDate(data.endIso)} per M of M-Sum (at
          the range's midpoint). Index 100 = class median.
          {data.notComparable > 0 && ` ${data.notComparable} not comparable.`}
        </p>
        <Segmented
          value={range}
          onChange={setRange}
          options={[
            { value: '30d', label: '30 days' },
            { value: '90d', label: '90 days' },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {CLASS_ORDER.map((key) => {
          const c = data.classes.find((x) => x.key === key)!;
          const meta = KILL_GROWTH_CLASS_META[key];
          return (
            <div key={key} className={cn('rounded-lg border px-3 py-2', meta.chip)}>
              <div className="text-[10px] font-semibold uppercase tracking-wider">{meta.label}</div>
              <div className="text-xl font-semibold tabular-nums text-slate-100">
                {formatEfficiency(c.medianEfficiency)}
              </div>
              <div className="text-[11px] text-slate-400">
                median +{fmtM(c.medianDeltaKillsM)} M kills · {c.players} players
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 text-[11px] text-slate-400">
        {Object.values(COMBAT_PROFILE_META).map((m) => (
          <span key={m.label} className="inline-flex items-center gap-1">
            <span className={cn('rounded border px-1.5 py-0.5 text-[10px]', m.chip)}>{m.label}</span>
            {m.hint}
          </span>
        ))}
      </div>

      <Panel
        title="Players"
        actions={
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All' },
              ...CLASS_ORDER.map((k) => ({ value: k, label: KILL_GROWTH_CLASS_META[k].label })),
            ]}
          />
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <Th left>Player</Th>
                <Th left>Class</Th>
                <Th onClick={() => setSort('index')} active={sort === 'index'}>
                  Index
                </Th>
                <Th>Kills / M</Th>
                <Th onClick={() => setSort('kills')} active={sort === 'kills'}>
                  +Kills
                </Th>
                <Th>M-Sum</Th>
                <Th onClick={() => setSort('growth')} active={sort === 'growth'}>
                  M-Sum growth
                </Th>
                <Th left>Profile</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = KILL_GROWTH_CLASS_META[r.class];
                const profile = r.profile ? COMBAT_PROFILE_META[r.profile] : null;
                return (
                  <tr key={r.player.id} className="border-t border-slate-800/60">
                    <td className="px-2 py-1">{playerLabel(r.player)}</td>
                    <td className="px-2 py-1 text-xs" style={{ color: meta.color }}>
                      {meta.label}
                    </td>
                    <td className="px-2 py-1 text-right font-semibold tabular-nums">
                      {r.index == null ? '—' : Math.round(r.index)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-300">
                      {formatEfficiency(r.efficiency)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {r.killsEstimated && '≈'}+{fmtM(r.deltaKillsM)} M
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-400">
                      {fmtM(r.mSum, 1)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-400">
                      {formatGrowthPct(r.growthPct)}
                    </td>
                    <td className="px-2 py-1">
                      {profile && (
                        <span
                          className={cn('rounded border px-1.5 py-0.5 text-[10px]', profile.chip)}
                          title={profile.hint}
                        >
                          {profile.label}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
